import { Model } from "99Tools/useModel";
import { CognitoUser } from "amazon-cognito-identity-js";
import { Auth } from "aws-amplify";
import { validate } from "email-validator";
import { ListParams } from "./DataTypes";

export class AccountSettings {
  public user: string;
  public pass: string;
  public passConfirm: string;
  public lastName: string;
  public firstName: string;
  public code: string;
  /** 法人名（adminのみ） */
  public companyName: string;
  /** 部署名（adminのみ） */
  public department: string;
  /** 電話番号（adminのみ） */
  public companyPhone: string;

  constructor(
    user?: string,
    pass?: string,
    passConfirm?: string,
    lastName?: string,
    firstName?: string,
    code?: string,
    companyName?: string,
    department?: string,
    companyPhone?: string
  ) {
    this.user = user ?? "";
    this.pass = pass ?? "";
    this.passConfirm = passConfirm ?? "";
    this.lastName = lastName ?? "";
    this.firstName = firstName ?? "";
    this.code = code ?? "";
    this.companyName = companyName ?? "";
    this.department = department ?? "";
    this.companyPhone = companyPhone ?? "";
  }
  public Copy() {
    const c = new AccountSettings();
    c.user = this.user.slice();
    c.pass = this.pass.slice();
    c.passConfirm = this.passConfirm.slice();
    c.lastName = this.lastName.slice();
    c.firstName = this.firstName.slice();
    c.code = this.code.slice();
    c.companyName = this.companyName.slice();
    c.department = this.department.slice();
    c.companyPhone = this.companyPhone.slice();
    return c;
  }
}

export type UserKind =
  | "super_administrator"
  | "administrator"
  | "sub_admin"
  | "manager"
  | "user"
  | "login";

export class UserNotificationFlag {
  public DisplayFlg: string = "false";
  public SensorId: string = "";
  public MailFlg: string = "false";
  public BuildingId: string = "";
  public LocationId: string = "";

  constructor(data?: any) {
    if (!data) {
      return;
    }
    this.DisplayFlg = data?.DisplayFlg ?? "false";
    this.SensorId = data?.SensorId ?? "";
    this.MailFlg = data?.MailFlg ?? "false";
    this.BuildingId = data?.BuildingId ?? "";
    this.LocationId = data?.LocationId ?? "";
  }
  public Copy() {
    const c = new UserNotificationFlag();
    c.DisplayFlg = this.DisplayFlg.slice();
    c.SensorId = this.SensorId.slice();
    c.MailFlg = this.MailFlg.slice();
    c.BuildingId = this.BuildingId.slice();
    c.LocationId = this.LocationId.slice();
    return c;
  }
}

/**
 * User編集用タイプ
 */
export type EditUserData = {
  LinkBuildings?: string[];
  Remarks?: string;
  UserId: string;
  Flags?: UserNotificationFlag;
  CompanyName?: string;
  DepartmentName?: string;
  PhoneNumber?: string;
};

export class User {
  public static urlUser = process.env.REACT_APP_URL_USERS ?? "";
  public static urlUserSettings =
    process.env.REACT_APP_URL_USERS_SETTINGS ?? "";
  public sub: string;
  public email_verified: string;
  public given_name: string;
  public family_name: string;
  public email: string;
  public UpdatedAt: string;
  public UserId: string;
  public UserStatus: string;
  public ValidItem: string;
  public Remarks: string;
  public ParentId: string;
  public AccountType: UserKind;
  public CreatedAt: string;
  public LinkBuildings: string[] = [];
  public SensorSettings: UserNotificationFlag[] = [];
  // 登録用
  public password: string = "";

  constructor(data?: any) {
    this.sub = data?.sub ?? "";
    this.email_verified = data?.email_verified ?? "";
    this.given_name = data?.given_name ?? "";
    this.family_name = data?.family_name ?? "";
    this.email = data?.email ?? "";
    this.UpdatedAt = data?.UpdatedAt ?? "";
    this.UserId = data?.UserId ?? "invalid";
    this.UserStatus = data?.UserStatus ?? "";
    this.ValidItem = data?.ValidItem ?? "";
    this.Remarks = data?.Remarks ?? "";
    this.ParentId = data?.ParentId ?? "";
    this.AccountType = data?.AccountType ?? "invalid";
    this.CreatedAt = data?.CreatedAt ?? "";
    this.LinkBuildings = data?.LinkBuildings ?? [];
    this.password = data?.password ?? "";
    this.SensorSettings =
      data?.SensorSettings?.map(
        (item: any) => new UserNotificationFlag(item)
      ) ?? [];
  }
  public Copy() {
    const c = new User();
    c.sub = this.sub.slice();
    c.email_verified = this.email_verified.slice();
    c.given_name = this.given_name.slice();
    c.family_name = this.family_name.slice();
    c.email = this.email.slice();
    c.UpdatedAt = this.UpdatedAt.slice();
    c.UserId = this.UserId.slice();
    c.ValidItem = this.ValidItem.slice();
    c.Remarks = this.Remarks.slice();
    c.ParentId = this.ParentId.slice();
    c.AccountType = this.AccountType.slice() as UserKind;
    c.CreatedAt = this.CreatedAt.slice();
    c.LinkBuildings = this.LinkBuildings.map((item: string) => item.slice());
    c.SensorSettings = this.SensorSettings.map((item: UserNotificationFlag) =>
      item.Copy()
    );
    c.password = this.password.slice();
    return c;
  }
  /**
   * ユーザ一覧を取得
   * @returns ユーザ一覧
   */
  public static async GetUsers(): Promise<User[]> {
    const session = await Auth.currentSession();
    if (!session) {
      throw new Error("ログインしていない");
    }
    const token = session.getIdToken().getJwtToken();
    const options: RequestInit = {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: token,
      }),
    };
    return await fetch(User.urlUser, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error("ユーザー情報取得失敗");
        }
        return response.json();
      })
      .then((data) => {
        return data.map((item: any) => new User(item));
      });
  }
  /**
   * 登録
   */
  public async RegisterUser() {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const options: RequestInit = {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: token,
      }),
      body: `{"FamilyName":"${this.family_name}","Remarks":"${this.Remarks}","EMail":"${this.UserId}","GivenName":"${this.given_name}","AccountType":"${this.AccountType}"}`,
    };
    await fetch(User.urlUser, options).then((response) => {
      if (!response.ok) {
        throw new Error("アカウント情報登録失敗");
      }
    });
  }
  /**
   * ユーザ削除
   */
  public async DeleteUser() {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const options: RequestInit = {
      method: "DELETE",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: token,
      }),
      body: `{"UserId":"${this.UserId}"}`,
    };
    await fetch(User.urlUser, options).then((response) => {
      if (!response.ok) {
        throw new Error("アカウント情報削除失敗");
      }
    });
  }
  /**
   * ユーザ設定
   */
  public static async EditUser(data: EditUserData) {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const options: RequestInit = {
      method: "PUT",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: token,
      }),
      body: JSON.stringify(data),
    };
    await fetch(User.urlUser, options).then((response) => {
      if (!response.ok) {
        throw new Error("アカウント情報更新失敗");
      }
    });
  }
  /**
   * 紐づいたデバイス情報を取得
   */
  public async GetDetails() {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const options: RequestInit = {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: token,
      }),
      body: `{"UserId":"${this.UserId}"}`,
    };
    this.SensorSettings = await fetch(User.urlUserSettings, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error("アカウント詳細情報取得失敗");
        }
        return response.json();
      })
      .then((data) => {
        const users = data.map((item: any) => new User(item));
        const user = users.find((user: User) => user.UserId === this.UserId);
        return user?.SensorSettings ?? [];
      });
  }
}

export class UserNotification {
  public user: User = new User();
  public items: NotificationItem[] = [];
  constructor(user?: User) {
    if (!user) {
      return;
    }
    this.user = user;
  }
}

export type NotificationDeviceType = "pico3" | "sensor" | "invalid";
export class NotificationItem {
  public deviceKind: NotificationDeviceType = "invalid";
  public deviceName: string = "";
  public buildingName: string = "";
  public locationName: string = "";
  public flags: UserNotificationFlag = new UserNotificationFlag();
  constructor(
    deviceKind: NotificationDeviceType = "invalid",
    deviceName: string = "",
    buildingName: string = "",
    locationName: string = "",
    flags: UserNotificationFlag = new UserNotificationFlag()
  ) {
    this.deviceKind = deviceKind;
    this.deviceName = deviceName;
    this.buildingName = buildingName;
    this.locationName = locationName;
    this.flags = flags;
  }

  /**
   * ユーザ通知設定
   */
  public async EditUserNotification(user: User) {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const data = {
      UserId: user.UserId,
      Flags: this.flags,
    };
    const options: RequestInit = {
      method: "PUT",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: token,
      }),
      body: JSON.stringify(data),
    };
    await fetch(User.urlUser, options).then((response) => {
      if (!response.ok) {
        throw new Error("アカウント情報更新失敗");
      }
    });
  }
}

/**
 * アカウント設定画面用
 */
export class PageUsers extends Model {
  public bShowRegisterDlg = false;
  public bShowDetailDlg = false;
  public list = new ListParams();
  public reset() {
    this.bShowRegisterDlg = false;
    this.bShowDetailDlg = false;
    this.list.reset();
  }
}

/**
 * 通知設定画面用
 */
export class PageNotification extends Model {
  public bShowDetailDlg = false;
  public list = new ListParams();
  public reset() {
    this.bShowDetailDlg = false;
    this.list.reset();
  }
}

export class Account {
  public urlUser = process.env.REACT_APP_URL_USERS ?? "";
  public kind: UserKind = "login";

  public IsAdmin() {
    return this.kind === "super_administrator";
  }
  public IsUser() {
    return (
      this.kind === "administrator" ||
      this.kind === "sub_admin" ||
      this.kind === "manager" ||
      this.kind === "user"
    );
  }
  public IsUserAdmin() {
    return this.kind === "administrator";
  }
  public IsUserSubAdmin() {
    return this.kind === "sub_admin";
  }
  public IsUserManager() {
    return this.kind === "manager";
  }
  public IsUserViewer() {
    return this.kind === "user";
  }

  /**
   * サインアップ
   * @param account
   */
  public async SignUp(account: AccountSettings) {
    const params = {
      username: account.user,
      password: account.pass,
      attributes: {
        family_name: account.lastName,
        given_name: account.firstName,
        "custom:company_name": account.companyName,
        "custom:department_name": account.department,
        phone_number: account.companyPhone
          ? account.companyPhone.replace(/^0/, "+81")
          : null,
      },
    };
    await Auth.signUp(params);
  }

  /**
   * コード確認
   * @param account
   * @param code
   */
  public async ConfirmSignUp(account: AccountSettings) {
    await Auth.confirmSignUp(account.user, account.code);
  }

  /**
   * コード再送信
   * @param account
   */
  public async ResendConfirmCode(account: AccountSettings) {
    await Auth.resendSignUp(account.user);
  }

  /**
   * パスワード忘れ
   * @param account
   */
  public async ForgotPassword(account: AccountSettings) {
    await Auth.forgotPassword(account.user);
  }

  /**
   * パスワード忘れ時のパスワード再設定
   * @param account
   * @param code
   */
  public async ForgotPasswordSubmit(account: AccountSettings) {
    await Auth.forgotPasswordSubmit(account.user, account.code, account.pass);
  }

  /**
   * サインイン
   * @param account
   * @return signIn response
   */
  public async SignIn(account: AccountSettings) {
    if (!validate(account.user)) {
      throw new Error("Invalid mail address.");
    }
    const resp = await Auth.signIn(account.user, account.pass);
    return resp;
  }

  /**
   * サインアウト
   */
  public async SignOut() {
    this.kind = "login";
    localStorage.removeItem("cognitoUser");
    return Auth.signOut({ global: true });
  }

  /**
   * アカウント種別チェック
   * @param userId
   */
  public async CheckAccount(userId: string) {
    const users = await User.GetUsers();
    const user = users.filter((item: User) => userId === item.UserId);
    if (user.length === 0) {
      throw new Error("ユーザー情報が存在しない");
    }
    switch (user[0].AccountType) {
      case "super_administrator":
      case "administrator":
      case "sub_admin":
      case "manager":
      case "user":
        this.kind = user[0].AccountType;
        break;
      default:
        throw new Error("ユーザー資格情報不備");
    }
  }

  /**
   * ログインユーザのUserIdを返す
   * @returns UserId
   */
  public async GetLoginUserId(): Promise<string> {
    const userInfo = await Auth.currentUserInfo();
    return userInfo?.attributes?.sub ?? "";
  }

  /**
   * ログイン中かどうか
   * @params IsQualifiedUser ページを表示する資格があるユーザかどうか
   */
  public async IsLogin(IsQualifiedUser: (kind: UserKind) => boolean) {
    const userId = await this.GetLoginUserId();
    const users = await User.GetUsers();
    const user = users.filter((item: User) => userId === item.UserId);
    if (user.length === 0) {
      throw new Error("ユーザー情報が存在しない");
    }
    if (!IsQualifiedUser(user[0].AccountType)) {
      this.kind = "login";
      throw new Error("資格情報が不一致");
    }
    // リロードの場合などにアカウント情報を保持
    this.kind = user[0].AccountType;
  }

  public async ChangePassword(oldPassword: string, newPassword: string) {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword);
  }

  /**
   * アカウント名更新（Adminユーザ以外）
   * @param lastName
   * @param firstName
   */
  public async ChangeNames(lastName: string, firstName: string) {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.updateUserAttributes(user, {
      family_name: lastName,
      given_name: firstName,
    });
  }

  public async GetAttributes() {
    const user = await Auth.currentUserInfo();
    return new AccountSettings(
      user.attributes.email,
      "",
      "",
      user.attributes.family_name,
      user.attributes.given_name,
      undefined,
      user.attributes["custom:company_name"],
      user.attributes["custom:department_name"],
      user.attributes["phone_number"]
    );
  }

  public async DeleteUser() {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    const user = await Auth.currentUserInfo();
    const options: RequestInit = {
      method: "DELETE",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: token,
      }),
      body: `{"UserId":"${user.attributes.sub}"}`,
    };
    await fetch(this.urlUser, options).then((response) => {
      if (!response.ok) {
        throw new Error("アカウント情報削除失敗");
      }
    });
  }

  /**
   * 初期パスワード変更
   * @param user CognitoUser
   * @param newPassword 変更後パスワード
   * @returns
   */
  public async completeNewPassword(user: object, newPassword: string) {
    return await Auth.completeNewPassword(user, newPassword);
  }

  /**
   * TOTP 設定
   * @param user CognitoUser
   * @returns TOTP 登録コード
   */
  public async setupTOTP(user: CognitoUser) {
    return await Auth.setupTOTP(user);
  }

  /**
   * TOTP コード 認証
   * @param user CognitoUser
   * @param code TOTP code
   * @returns
   */
  public async confirmSignIn(user: object, code: string) {
    return Auth.confirmSignIn(user, code, "SOFTWARE_TOKEN_MFA");
  }

  /**
   * TOTP コード検証
   * @param user CognitoUser
   * @param code TOTP code
   * @returns
   */
  public async verifyTotpToken(user: object, code: string) {
    return await Auth.verifyTotpToken(user, code);
  }

  /**
   * 現在の CognitoUser 取得
   * @returns {CognitoUser}
   */
  public async getCurrentUser() {
    return await Auth.currentUserPoolUser();
  }

  /**
   * MFA アクティブ状態取得
   * @returns {string}
   */
  public async getPrefferedMfa() {
    const user = await Auth.currentAuthenticatedUser();
    return await Auth.getPreferredMFA(user);
  }

  /**
   * MFA アクティブ状態切替
   * @param method "TOTP" | "NOMFA"
   * @returns {string}
   */
  public async setPrefferedMfa(method: string) {
    const user = await Auth.currentAuthenticatedUser();
    return await Auth.setPreferredMFA(user, method as "TOTP" | "NOMFA");
  }

  /**
   * メールアドレス変更
   *
   * Cognito から変更先アドレスに認証コードが送信されます。
   *
   * @param email
   * @returns {string}
   */
  public async changeMail(email: string) {
    const user = await Auth.currentAuthenticatedUser();
    return await Auth.updateUserAttributes(user, { email });
  }

  /**
   * メールアドレス変更の認証コード確認
   * @param code
   * @returns {string}
   */
  public async verifyChangeMailCode(code: string) {
    return await Auth.verifyCurrentUserAttributeSubmit("email", code);
  }
}

/**
 * 法人情報
 */
export type CompanyInfo = {
  companyName: string;
  department: string;
  companyPhone: string;
};
